import { Controller, useFormContext } from "react-hook-form";
import EditorInput, { EditorInputProps } from "../EditorInput";
import useProgram from "../../../../utils/hooks/programs/useProgram";
import usePrograms from "../../../../utils/hooks/programs/usePrograms";
import { useEffect, useMemo, useState } from "react";
import { useStoreDispatch } from "../../../../utils/hooks/hooks";
import { CTBProgram } from "../../../../utils/api/endpoints/programs/parsePrograms";
import useTableDropdown from "../../../table/useTableDropdown";
import ProfileQuickLook from "../../../profile/ProfileQuickLook";

const ProgramSelector = ({
  value,
  onChange
}: {
  value?: number;
  onChange: (value: number) => void;
}) => {
  const dispatch = useStoreDispatch();
  const [search, setSearch] = useState("");
  const [update, setUpdate] = useState(true);
  const { programs, setFilters } = usePrograms();
  const { program, isLoading } = useProgram(value);

  const programOptions = useMemo(
    () =>
      (programs.filter(p => p?.id !== undefined) as CTBProgram[]).map(
        (program, index) => ({
          value: program.id,
          content: (
            <ProfileQuickLook
              key={index}
              title={program?.title}
              subtitle={program.creator?.displayName || ""}
              imageSrc={program.creator?.pfp}
              imageClassName="bg-ctb-blue-50"
            />
          )
        })
      ),
    [programs]
  );

  const { dropdown, setValue, focused } = useTableDropdown({
    name: "programs",
    placeholder: value && isLoading ? "Loading program..." : "Start typing to search programs...",
    options: programOptions,
    columns: [
      {
        name: "Program Name"
      }
    ],
    onSearch: value => setSearch(value),
    onSubmit: value => {
      onChange(value);
      setUpdate(true);
    }
  });

  useEffect(() => {
    if (update && program) {
      setUpdate(false);
      setValue(program.title);
    }
  }, [program, setValue, update]);

  // Restore the selected program's title when the dropdown is closed/unfocused
  useEffect(() => {
    if (!focused && program) {
      setValue(program.title);
    }
  }, [focused, program, setValue]);

  useEffect(() => {
    setFilters({
      limit: 5,
      search
    });
  }, [dispatch, search, setFilters]);

  return <>{dropdown}</>;
};

const ReportProgramSelector = ({
  name,
  inputTitle,
  details,
  required
}: EditorInputProps) => {
  const { control } = useFormContext();

  return (
    <EditorInput
      name={name}
      inputTitle={inputTitle}
      details={details}
      required={required}
    >
      <Controller
        name={name}
        control={control}
        rules={{
          required
        }}
        render={({ field: { value, onChange } }) => (
          <ProgramSelector value={value} onChange={onChange} />
        )}
      />
    </EditorInput>
  );
};

export default ReportProgramSelector;
